<script setup lang="ts">
/**
 * Appoint Director (B32101) 스튜디오 패키지 — Stage 1 Directors' Resolution(1부) · Stage 2 Form 45(선임 이사별 1부).
 *   Form 45 는 DocScope(f45_{id}/) 로 인물별 격리 — B32111 과 같은 저장 형식이라 파이썬 BM03B E-Filing
 *   (AppointWithdrawOfficerEFiling) 이 presetForm.values / local.newIds 로 그대로 시드한다.
 */
import { computed, markRaw, provide, ref, watch } from 'vue';
import type { Component } from 'vue';
import DocScope from '@/features/startTask/my_preset/sg/aura/agm/cdd/DocScope.vue';
import { useAgmFill } from '@/features/startTask/my_preset/sg/aura/agm/cdd/useCddFill';
import AppointDirectorDrDoc from './AppointDirectorDrDoc.vue';
import Form45Doc from '../../appoint-withdraw-director/studio/Form45Doc.vue';
import { directorIdLabel } from '../composables/directorIdLabel';

const props = defineProps<{ autoLocal?: { newIds?: number[] } | null }>();
const fill = useAgmFill();

const newIds = ref<number[]>([1]);
let newSeq = 1;
watch(() => props.autoLocal, (v) => {
  if (!v || !Array.isArray(v.newIds)) return;
  newIds.value = [...v.newIds];
  newSeq = v.newIds.length ? Math.max(...v.newIds) : 0;
}, { deep: true, immediate: true });

// DR 선임 목록 = 각 Form 45 사본의 이름·NRIC.
const st = (v: unknown) => String(v ?? '').trim();
provide('adPeople', computed(() => newIds.value
  .map((id) => ({
    name: st(fill.values[`f45_${id}/director_name`]),
    nric: st(fill.values[`f45_${id}/director_identification_no`]),
    label: directorIdLabel(fill.values[`f45_${id}/director_id_type`], fill.values[`f45_${id}/director_identification_no`]),
  }))
  .filter((r) => r.name)));

interface Slot { icon: string; name: string; comp: Component; scope?: string; id?: number; fixed?: boolean }
interface StageDef { no: number; en: string; short: string; toolLabel?: string; slots: Slot[] }
// ⚠ 상수 배열 + v-show 로는 Next 를 눌러도 Stage 2 가 display:none 그대로였다 → computed + class 바인딩.
const stages = computed<StageDef[]>(() => [
  { no: 1, en: "Directors' Resolution (Appoint Director).docx", short: 'DR',
    slots: [{ icon: '📄', name: "Directors' Resolution (Appoint Director).docx", comp: markRaw(AppointDirectorDrDoc), fixed: true }] },
  { no: 2, en: 'Form 45.docx  (per appointed director)', short: 'Form 45', toolLabel: '+ Add director',
    slots: newIds.value.map((id) => ({ icon: '🧾', name: 'Form 45.docx', comp: markRaw(Form45Doc), scope: `f45_${id}/`, id })) },
]);
function add() { newSeq += 1; newIds.value.push(newSeq); }
function remove(id: number) { newIds.value = newIds.value.filter((x) => x !== id); }

const currentStage = ref(1);
const totalStages = computed(() => stages.value.length);
function go(n: number) { if (n >= 1 && n <= totalStages.value) currentStage.value = n; }
const view = computed(() => {
  let n = 0;
  return stages.value.map((s) => ({ ...s, docs: s.slots.map((d) => ({ ...d, no: (n += 1) })) }));
});
const totalDocs = computed(() => view.value.reduce((s, x) => s + x.docs.length, 0));

const ZOOM_MIN = 100, ZOOM_MAX = 175, ZOOM_STEP = 25;
const docZoom = ref(100);
function setZoom(n: number) { docZoom.value = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(n / ZOOM_STEP) * ZOOM_STEP)); }

function gatherLocalState() { return { newIds: [...newIds.value], currentStage: currentStage.value }; }
function restoreLocalState(s: { newIds?: number[]; currentStage?: number } | null) {
  if (!s) return;
  if (Array.isArray(s.newIds) && s.newIds.length) { newIds.value = [...s.newIds]; newSeq = Math.max(...s.newIds); }
  if (s.currentStage) go(s.currentStage);
}
defineExpose({ gatherLocalState, restoreLocalState });
</script>

<template>
  <div class="ad-pkg">
    <div class="stepper">
      <button v-for="st2 in view" :key="st2.no" type="button" class="step" :class="{ active: currentStage === st2.no, done: currentStage > st2.no }" @click="go(st2.no)">
        <span class="step-no">{{ currentStage > st2.no ? '✓' : st2.no }}</span>
        <span class="step-txt"><b>Stage {{ st2.no }}</b><small>{{ st2.short }}</small></span>
      </button>
    </div>
    <div class="zoom-bar">
      <div class="doc-zoom">
        <button type="button" class="doc-zoom-btn" :disabled="docZoom <= ZOOM_MIN" @click="setZoom(docZoom - ZOOM_STEP)">A−</button>
        <button type="button" class="doc-zoom-pct" @click="setZoom(100)">{{ docZoom }}%</button>
        <button type="button" class="doc-zoom-btn" :disabled="docZoom >= ZOOM_MAX" @click="setZoom(docZoom + ZOOM_STEP)">A+</button>
      </div>
    </div>
    <div class="paper-zoom" :style="{ zoom: docZoom / 100 }">
      <section v-for="st2 in view" :key="st2.no" class="stage" :class="{ 'stage-off': currentStage !== st2.no }">
        <div class="stage-head"><span class="stage-badge">Stage {{ st2.no }}</span><span class="stage-t">{{ st2.en }}</span></div>
        <div v-if="st2.toolLabel" class="ad-toolbar">
          <span class="ad-toolbar-lbl">Add copy (one per appointed director)</span>
          <button type="button" class="ad-tool" @click="add">{{ st2.toolLabel }}</button>
        </div>
        <div v-if="st2.toolLabel && !st2.docs.length" class="ad-empty">No directors — use <b>{{ st2.toolLabel }}</b> above.</div>
        <div v-for="d in st2.docs" :key="d.fixed ? 'fixed' : d.scope" class="pkg-doc">
          <div class="pkg-doclabel">
            <span class="ic">{{ d.icon }}</span><span class="nm">{{ d.name }}</span>
            <template v-if="!d.fixed"><span class="copy">#{{ d.id }}</span><button type="button" class="ad-rm" title="Remove" @click="remove(d.id!)">×</button></template>
            <span class="no">Doc {{ d.no }} / {{ totalDocs }}</span>
          </div>
          <div class="simple-frame">
            <DocScope v-if="d.scope" :scope="d.scope"><component :is="d.comp" /></DocScope>
            <component v-else :is="d.comp" />
          </div>
        </div>
      </section>
    </div>
    <div class="stage-nav">
      <button v-if="currentStage > 1" type="button" class="nav-btn" @click="go(currentStage - 1)">← Previous</button>
      <span class="nav-count">Stage {{ currentStage }} / {{ totalStages }}</span>
      <button v-if="currentStage < totalStages" type="button" class="nav-btn primary" @click="go(currentStage + 1)">Next →</button>
      <button v-else type="button" class="nav-btn primary" disabled>All stages complete ✓</button>
    </div>
  </div>
</template>

<style scoped>
.ad-pkg { color: #111; }
.stepper { display: flex; gap: 8px; width: 794px; max-width: 100%; margin: 0 auto 12px; padding: 12px; background: #fff; border: 1px solid #e6ebf3; border-radius: 14px; box-shadow: 0 6px 18px rgba(20,30,60,0.06); box-sizing: border-box; }
.step { flex: 1 1 0; min-width: 0; display: flex; align-items: flex-start; gap: 8px; padding: 10px; border: 1px solid transparent; border-radius: 12px; background: #f6f8fc; cursor: pointer; text-align: left; }
.step:hover { background: #eef3fb; } .step.active { background: #eaf1ff; border-color: #b9d0ff; } .step.done { background: #eafaf1; }
.step-no { flex: none; width: 24px; height: 24px; border-radius: 50%; background: #c9d3e4; color: #fff; font-weight: 800; font-size: 11px; display: inline-flex; align-items: center; justify-content: center; margin-top: 1px; }
.step.active .step-no { background: #1b64da; } .step.done .step-no { background: #22a06b; }
.step-txt { display: flex; flex-direction: column; gap: 2px; min-width: 0; } .step-txt b { font-size: 11.5px; color: #1f2d4d; white-space: nowrap; } .step-txt small { font-size: 10px; color: #7a869c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.zoom-bar { display: flex; justify-content: flex-end; width: 794px; max-width: 100%; margin: 0 auto 18px; }
.doc-zoom { display: inline-flex; align-items: center; gap: 2px; background: #f2f4f6; border-radius: 10px; padding: 2px; }
.doc-zoom-btn, .doc-zoom-pct { border: none; background: transparent; cursor: pointer; font-weight: 800; color: #4b5563; border-radius: 8px; height: 32px; } .doc-zoom-btn { min-width: 36px; font-size: 15px; } .doc-zoom-btn:disabled { opacity: 0.35; cursor: default; } .doc-zoom-pct { min-width: 52px; font-size: 12px; color: #3182f6; }
.paper-zoom { width: 794px; max-width: 100%; margin: 0 auto; }
.stage { width: 100%; margin: 0 auto; }
.stage.stage-off { display: none; }
.stage-head { display: flex; align-items: center; gap: 12px; margin: 0 0 18px; padding: 4px 2px 14px; border-bottom: 1px solid #eef1f6; }
.stage-badge { flex: none; background: #1b64da; color: #fff; font-weight: 800; font-size: 12.5px; padding: 5px 12px; border-radius: 999px; } .stage-t { font-weight: 800; font-size: 15px; color: #1f2d4d; }
.ad-toolbar { width: 100%; margin: 0 0 22px; display: flex; flex-wrap: wrap; gap: 10px; align-items: center; padding: 14px 16px; border: 1px solid #d7e0f0; border-radius: 12px; background: #f7f9fd; box-sizing: border-box; }
.ad-toolbar-lbl { font-size: 11.5px; font-weight: 800; color: #6b7a94; }
.ad-tool { border: 1px solid #1b84ff; background: #fff; color: #1b84ff; border-radius: 999px; padding: 7px 13px; font-size: 12px; font-weight: 700; cursor: pointer; } .ad-tool:hover { background: #eaf3ff; }
.ad-empty { width: 100%; margin: 8px 0 18px; padding: 16px 18px; border: 1px dashed #c5d0e0; border-radius: 12px; background: #f7f9fc; color: #5b6b82; font-size: 13px; box-sizing: border-box; }
.pkg-doc { width: 100%; } .pkg-doc + .pkg-doc { margin-top: 36px; }
.pkg-doclabel { display: flex; align-items: center; gap: 8px; padding: 0 4px 12px; } .pkg-doclabel .ic { font-size: 14px; } .pkg-doclabel .nm { font-size: 12.5px; font-weight: 800; color: #42506a; }
.pkg-doclabel .copy { font-size: 10.5px; font-weight: 800; color: #1b84ff; background: #eaf3ff; border-radius: 999px; padding: 2px 8px; } .pkg-doclabel .no { margin-left: auto; font-size: 10.5px; font-weight: 800; color: #9aa6bd; white-space: nowrap; }
.ad-rm { border: none; background: transparent; color: #c0392b; font-size: 18px; cursor: pointer; }
.simple-frame { width: 100%; background: #fff; border: 1px solid #e2e5ea; box-shadow: 0 4px 20px rgba(20,30,60,0.09); padding: 32px; box-sizing: border-box; overflow: hidden; font-size: 12px; }
.stage-nav { display: flex; align-items: center; justify-content: center; gap: 16px; width: 794px; max-width: 100%; margin: 36px auto 12px; }
.nav-btn { border: 1px solid #d3dceb; background: #fff; color: #42506a; font-size: 13px; font-weight: 800; border-radius: 10px; padding: 11px 20px; cursor: pointer; } .nav-btn.primary { background: #1b64da; border-color: #1b64da; color: #fff; } .nav-btn:disabled { opacity: 0.55; cursor: default; }
.nav-count { font-size: 12.5px; font-weight: 800; color: #8a97b5; }
</style>
